import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

type Autonomy = Awaited<ReturnType<typeof api.get<'/api/v1/autonomy'>>>;
type Activity = Awaited<ReturnType<typeof api.get<'/api/v1/autonomy/activity'>>>;

const LEVELS = [0, 1, 2, 3] as const;
type Level = (typeof LEVELS)[number];

const CAPABILITIES = [
  'organise',
  'archive',
  'mark_read',
  'draft',
  'unsubscribe',
  'cleanup',
  'remind',
  'send',
  'delete',
] as const;

/**
 * How much Butler may do without asking.
 *
 * The screen that decides whether somebody keeps this product, and the design problem is that the
 * honest version of it is a settings page with nine dropdowns. Three things stop it being that.
 *
 * **Each level says what it costs to be wrong.** Not "level 2" but "Butler may unsubscribe you from
 * things, and an unsubscribe cannot be taken back". A number with no consequence attached is a
 * number people set by mood.
 *
 * **The pause is a button, not a dropdown.** Somebody who wants everything to stop is not in a
 * frame of mind to find the right row. It sits at the top and it outranks every level below it.
 *
 * **Refusals are shown.** "Butler did not do 34 things this week because it was not confident
 * enough" is the sentence that turns an invisible threshold into a setting somebody can reason
 * about — and it is the answer to the question people actually ask, which is why nothing happened.
 */
export function AutonomyScreen(): ReactNode {
  const { t } = useI18n();
  const { activeWorkspace } = useAuth();

  const [settings, setSettings] = useState<Autonomy | null>(null);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const [current, refusals] = await Promise.all([
          api.get('/api/v1/autonomy', undefined, { tenantId, signal }),
          api.get('/api/v1/autonomy/activity', undefined, { tenantId, signal }),
        ]);
        setSettings(current);
        setActivity(refusals);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        /* A failed load renders as a failure. Showing level 0 because the request failed would tell
           somebody Butler is doing nothing when it may be doing everything. */
        setSettings(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const save = useCallback(
    async (body: Parameters<typeof api.patch<'/api/v1/autonomy'>>[1]) => {
      if (tenantId === undefined) return;
      setBusy(true);
      try {
        const next = await api.patch('/api/v1/autonomy', body, undefined, { tenantId });
        setSettings(next);
        setFailure(null);
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [tenantId],
  );

  if (failure !== null && settings === null) {
    return (
      <Alert tone="critical" title={t('autonomy.error.title')}>
        <p>{t('autonomy.error.body')}</p>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (settings === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  const paused = settings.pausedUntil !== null && new Date(settings.pausedUntil) > new Date();

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('autonomy.title')}</h1>
        <p className="empty__body">{t('autonomy.intro')}</p>
      </header>

      {/*
        The stop button, above everything.

        Somebody who wants Butler to stop is not in a frame of mind to find the right dropdown, and
        making them set nine levels to zero to achieve it is the interface arguing with them.
      */}
      <section className="card">
        <h2 className="card__title">{t('autonomy.pause.title')}</h2>
        <p className="empty__body">
          {paused ? t('autonomy.pause.active') : t('autonomy.pause.body')}
        </p>
        <Button
          disabled={busy}
          onClick={() =>
            void save(
              paused
                ? { pausedUntil: null, pausedReasonCode: null }
                : {
                    pausedUntil: new Date(Date.now() + 7 * 86_400_000).toISOString(),
                    pausedReasonCode: 'requested_by_user',
                  },
            )
          }
        >
          {paused ? t('autonomy.pause.resume') : t('autonomy.pause.stop')}
        </Button>
      </section>

      <section className="card">
        <h2 className="card__title">{t('autonomy.levels.title')}</h2>
        <ul className="autonomy-levels">
          {LEVELS.map((level) => (
            <li key={level}>
              <strong>{t(`autonomy.level.${level}.title`)}</strong>
              {/* The consequence, beside the name. A number with nothing attached is set by mood. */}
              <span className="today-list__meta">{t(`autonomy.level.${level}.body`)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2 className="card__title">{t('autonomy.capabilities.title')}</h2>
        <ul className="today-list">
          {CAPABILITIES.map((capability) => {
            const required = settings.requiredLevels[capability] ?? 0;
            const effective = settings.capabilityLevels[capability] ?? settings.defaultLevel;

            return (
              <li className="today-list__item" key={capability}>
                <span className="today-list__subject">
                  {t(`autonomy.capability.${capability}`)}
                </span>
                <select
                  aria-label={t(`autonomy.capability.${capability}`)}
                  disabled={busy}
                  onChange={(event) =>
                    void save({
                      capabilityLevels: {
                        [capability]: Number(event.target.value) as Level,
                      },
                    })
                  }
                  value={effective}
                >
                  {LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {t(`autonomy.level.${level}.title`)}
                    </option>
                  ))}
                </select>
                {/*
                  Saying what it needs, rather than letting somebody set a level that will do
                  nothing and wonder why. The required level comes from the server so there is one
                  copy of the table.
                */}
                {effective < required && (
                  <span className="today-list__meta">
                    {t('autonomy.capability.needs', { level: required })}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="card">
        <h2 className="card__title">{t('autonomy.refusals.title')}</h2>
        {activity === null || activity.refusals.length === 0 ? (
          <p className="empty__body">{t('autonomy.refusals.none')}</p>
        ) : (
          <ul className="today-stats">
            {activity.refusals.map((refusal) => (
              <li key={refusal.refusalCode}>
                {t(`autonomy.refusal.${refusal.refusalCode}`, { count: refusal.count })}
              </li>
            ))}
          </ul>
        )}
      </section>

      {failure !== null && (
        <Alert tone="critical" title={t('autonomy.error.title')}>
          {t('autonomy.error.body')}
        </Alert>
      )}
    </>
  );
}
